import { AxiosPromise, AxiosStatic } from "axios";

export type IsaWrapCurrency = "GBP" | "USD";

/**
 * Error codes returned by the ISA wrap endpoints
 * (`GET /reallocations/isa-wrap/eligibility` and
 * `POST /reallocations/isa-wrap`). Mirrors the backend's
 * `ISA_WRAP_ERROR_CODES` enum.
 */
export const ISA_WRAP_ERROR_CODES = {
  SOURCE_COMMITMENT_NOT_FOUND: "SOURCE_COMMITMENT_NOT_FOUND",
  SOURCE_NOT_OWNED: "SOURCE_NOT_OWNED",
  SOURCE_NOT_PERSONAL: "SOURCE_NOT_PERSONAL",
  DESTINATION_COMMITMENT_NOT_FOUND: "DESTINATION_COMMITMENT_NOT_FOUND",
  DESTINATION_NOT_OWNED: "DESTINATION_NOT_OWNED",
  DESTINATION_NOT_ISA: "DESTINATION_NOT_ISA",
  NOTICE_PERIOD_REQUIRED: "NOTICE_PERIOD_REQUIRED",
  NOTICE_PERIOD_NOT_APPLICABLE: "NOTICE_PERIOD_NOT_APPLICABLE",
  DESTINATION_DEAL_UNAVAILABLE: "DESTINATION_DEAL_UNAVAILABLE",
  CURRENCY_NOT_SUPPORTED: "CURRENCY_NOT_SUPPORTED",
  INVESTOR_NO_ISA_ACCOUNT: "INVESTOR_NO_ISA_ACCOUNT",
  ISA_ACCOUNT_PAUSED: "ISA_ACCOUNT_PAUSED",
  AMOUNT_EXCEEDS_ISA_ALLOWANCE: "AMOUNT_EXCEEDS_ISA_ALLOWANCE",
} as const;

export type IsaWrapErrorCode =
  (typeof ISA_WRAP_ERROR_CODES)[keyof typeof ISA_WRAP_ERROR_CODES];

/**
 * Shape of the error response body returned by the ISA wrap endpoints
 * when validation fails. `inFlightPendingTotal` is populated on
 * `AMOUNT_EXCEEDS_ISA_ALLOWANCE` so the consumer can differentiate
 * between "annual allowance fully used" (== 0) and "remaining
 * allowance reserved by a PENDING wrap" (> 0).
 */
export interface IsaWrapErrorBody {
  code: IsaWrapErrorCode;
  message?: string;
  inFlightPendingTotal?: number;
}

export interface IsaWrapNoticePeriodOption {
  months: 3 | 6 | 12;
  destinationDealId: string;
  label: string;
  baseRate: number;
  loyaltyBoost: number | null;
  memberRate: number | null;
  isBestRate: boolean;
}

export interface IsaWrapPersonalCommitment {
  commitmentId: string;
  paidBalance: number;
  availableBalance: number;
}

export interface IsaWrapEligibleDeal {
  dealId: string;
  dealName: string;
  currency: IsaWrapCurrency;
  personalBalance: number;
  availableBalance: number;
  isaBalance: number;
  personalCommitments: IsaWrapPersonalCommitment[];
  isaCommitmentId: string | null;
  isLegacyFund: boolean;
  noticePeriodMonths?: 3 | 6 | 12;
  noticePeriodOptions?: IsaWrapNoticePeriodOption[];
  loyaltyDeadline?: string;
  reallocationDeadline?: string | null;
  webinarRecordingUrl?: string | null;
}

export interface IsaWrapEligibilityResponse {
  ifIsaAccountId: string;
  taxYear: string;
  availableToInvest: number;
  eligibleDeals: IsaWrapEligibleDeal[];
}

export const getIsaWrapEligibility =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (): AxiosPromise<IsaWrapEligibilityResponse> =>
    await appAxios.get(`${serverURL}/reallocations/isa-wrap/eligibility`, {
      withCredentials: true,
    });

export interface IsaWrapCreateInput {
  sourceCommitmentId: string;
  sourceAmount: number;
  destinationCommitmentId?: string;
  destinationAmount?: number;
  notes?: string;
  destinationNoticePeriodMonths?: 3 | 6 | 12;
}

export type IsaWrapReallocationStatus =
  | "PENDING"
  | "TODO"
  | "IN_PROGRESS"
  | "CONFIRMED"
  | "CANCELLED"
  | "DELETED";

export interface IsaWrapReallocationCommitment {
  id: string;
  paymentReference?: string;
  deal: {
    id: string;
    name?: string;
  };
}

export interface IsaWrapReallocationNote {
  message: string;
  createdAt: string;
  createdBy: string;
}

export interface IsaWrapReallocation {
  id: string;
  status: IsaWrapReallocationStatus;
  sourceAmount: number;
  destinationAmount: number | null;
  sourceCommitment: IsaWrapReallocationCommitment;
  destinationCommitment: IsaWrapReallocationCommitment | null;
  notes: IsaWrapReallocationNote[] | null;
  created_at: string;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: string | null;
}

export const submitIsaWrap =
  (serverURL: string, appAxios: AxiosStatic) =>
  async (data: IsaWrapCreateInput): AxiosPromise<IsaWrapReallocation> =>
    await appAxios.post(`${serverURL}/reallocations/isa-wrap`, data, {
      withCredentials: true,
    });
